import { Add, Info } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  ClickAwayListener,
  FormControl,
  Paper,
  Popper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Location } from "history";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Controller, useFormState } from "react-hook-form";
import { Prompt, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { HierarchicalAutocomplete } from "@/Components/v2/atoms/Autocomplete";
import { EnhancedOption } from "@/Components/v2/atoms/Autocomplete/types";
import { Breadcrumbs } from "@/Components/v2/atoms/Breadcrumbs";
import { OutlinedButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { Divider } from "@/Components/v2/atoms/Divider";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { DollarTextField } from "@/Components/v2/atoms/TextField";
import { Tooltip } from "@/Components/v2/atoms/ToolTip";
import { BaseTypography } from "@/Components/v2/atoms/Typography";
import { brandColors } from "@/Components/v2/utils/brand-colors";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useConfig } from "@/hooks/useConfig";
import { confirmUnsavedChanges } from "@/pages/scenario-builder/v3/components/UnsavedChangesConfirmation";
import { isDefined } from "@/Utils/array-utils";

import { getScenarioV3FormRoute, useScenariosV3Routes } from "../../hooks/useScenariosV3Routes";
import { bcpLayerKey } from "../../utils/bcp";
import { stringToNumber } from "../../utils/stringToNumber";
import { usePageVisitTracking, useScenarioAnalyticsV3 } from "../../utils/useScenarioAnalyticsV3";
import {
  ConstraintsMaxTotalExceedsTotalBudgetErrorKey,
  ConstraintsMinTotalExceedsTotalBudgetErrorKey,
  FormGeographyStatus,
  useScenarioForms,
} from "../scenario-form/ScenarioFormProvider";
import { MediaConstraint, ScenarioTypeEnum, SpendInput } from "../scenario-form/types";
import ForecastConstraintsTable from "./ForecastContraintsTable";
import ForecastGeographySpendsTable from "./ForecastGeographySpendsTable";
import InfoBanner from "./InfoBanner";
import OptimizerConstraintsTable from "./OptimizerContraintsTable";
import OptimizerGeographyConstraintsTable from "./OptimizerGeographyConstraintsTable";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useFeatureGate } from "@statsig/react-bindings";
import { TOP_NAV_CONTENT_MAX_WIDTH_PX, TOP_NAV_CONTENT_MIN_WIDTH_PX } from "@/Utils/GlobalStyle";

interface RouteParams {
  uuid: string;
}

const defaultGeographyFromGeoStatus = (geoStatus: FormGeographyStatus) => {
  if (geoStatus.type === "disabled") {
    return null;
  }

  if (geoStatus.type === "single_geography") {
    return geoStatus.geography;
  }

  if (geoStatus.spendGeographicGranularity === "all_geographies") {
    return null;
  }

  return ""; // Geography to be set by the user
};

const ChannelSpendPage: React.FC = () => {
  const { uuid } = useParams<RouteParams>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { config } = useConfig();
  const theme = useTheme();

  const { value: isConstraintsFromApiEnabled } = useFeatureGate(
    featureFlag.scenarioBuilderConstraintsFromApi
  );

  // Simple UI state management
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    historicalSpendsDTO,
    mediaConstraintsForm,
    spendInputForm,
    scenarioInformationForm,
    geoStatus,
    loading,
    scenario,
    budgetAllocationForm,
    historicalSpends,
    saveForm,
    isSaving,
    handleTotalBudgetChange,
    handleMediaConstraintsRowChange,
    handleSpendInputRowChange,
    distinctBCP,
  } = useScenarioForms();

  const showGeoTable =
    geoStatus.type === "all_geographies" &&
    geoStatus.spendGeographicGranularity === "single_geography";

  const mediaConstraints = mediaConstraintsForm.watch("mediaConstraints");
  const spendInputs = spendInputForm.watch("spendInputs");

  const { mapBcpName } = useDefaultNodeNameInfo();

  const mediaConstraintsFormState = useFormState({
    control: mediaConstraintsForm.control,
    name: "mediaConstraints",
  });

  const spendInputFormState = useFormState({
    control: spendInputForm.control,
    name: "spendInputs",
  });

  const isDirty = useMemo(() => {
    if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
      return mediaConstraintsFormState.isDirty;
    }

    if (scenario?.scenarioType === ScenarioTypeEnum.Budget) {
      return spendInputFormState.isDirty;
    }

    return false;
  }, [scenario?.scenarioType, spendInputFormState.isDirty, mediaConstraintsFormState.isDirty]);

  const selectedBCP = useMemo(() => {
    if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
      if (!mediaConstraints) {
        return new Map();
      }

      return mediaConstraints.reduce(
        (
          acc: Map<string, { brand: string; category: string; product: string }>,
          constraint: MediaConstraint
        ) => {
          const key = bcpLayerKey(constraint);
          if (!acc.has(key)) {
            acc.set(key, {
              brand: constraint.brand,
              category: constraint.category,
              product: constraint.product,
            });
          }
          return acc;
        },
        new Map<string, { brand: string; category: string; product: string }>()
      );
    } else {
      if (!spendInputs) {
        return new Map();
      }

      return spendInputs.reduce(
        (
          acc: Map<string, { brand: string; category: string; product: string }>,
          input: SpendInput
        ) => {
          const key = bcpLayerKey(input);
          if (!acc.has(key)) {
            acc.set(key, {
              brand: input.brand,
              category: input.category,
              product: input.product,
            });
          }
          return acc;
        },
        new Map<string, { brand: string; category: string; product: string }>()
      );
    }
  }, [scenario?.scenarioType, mediaConstraints, spendInputs]);

  const businessUnitHierarchyOptions = useMemo(() => {
    const hierarchy = new Map<string, Map<string, Set<string>>>();

    Array.from(distinctBCP?.values() || []).forEach(({ brand, category, product }) => {
      if (!hierarchy.has(brand)) {
        hierarchy.set(brand, new Map());
      }
      const categoryMap = hierarchy.get(brand)!;

      if (!categoryMap.has(category)) {
        categoryMap.set(category, new Set());
      }
      const productSet = categoryMap.get(category)!;

      if (product !== "default") {
        productSet.add(product);
      }
    });

    const EndAdornment = () => {
      return (
        <Tooltip title="No historical spend data available" arrow>
          <Info sx={{ fontSize: 16, color: "error.main" }} />
        </Tooltip>
      );
    };

    return Array.from(hierarchy.entries()).map(([brand, categoryMap]) => {
      const nonDefaultCategories = Array.from(categoryMap.entries()).filter(
        ([category]) => category !== "default"
      );
      const defaultProducts = categoryMap.get("default") || new Set();

      // Collect all products that should be at the brand level
      const defaultCategoryProducts = Array.from(defaultProducts);

      // Create the brand node
      const brandNode = {
        value: bcpLayerKey({ brand, category: "default", product: "default" }),
        label: mapBcpName({ brand, category: "default", product: "default" }).brand,
        id: stringToNumber(brand),
        alwaysOpen: true,
        selectable: distinctBCP?.has(
          bcpLayerKey({ brand, category: "default", product: "default" })
        ),
        endAdornment: !distinctBCP?.has(
          bcpLayerKey({ brand, category: "default", product: "default" })
        ) && <EndAdornment />,
        children: [
          // Add products that were under "default" category directly under brand
          ...defaultCategoryProducts.map((product) => ({
            value: bcpLayerKey({ brand, category: "default", product }),
            label: mapBcpName({ brand, category: "default", product }).product,
            id: stringToNumber(product),
            alwaysOpen: true,
            selectable: distinctBCP?.has(bcpLayerKey({ brand, category: "default", product })),
            endAdornment: !distinctBCP?.has(
              bcpLayerKey({ brand, category: "default", product })
            ) && <EndAdornment />,
          })),
          // Add valid categories with their products
          ...nonDefaultCategories.map(([category, productSet]) => ({
            value: bcpLayerKey({ brand, category, product: "default" }),
            label: mapBcpName({ brand, category, product: "default" }).category,
            id: stringToNumber(category),
            alwaysOpen: true,
            selectable: distinctBCP?.has(bcpLayerKey({ brand, category, product: "default" })),
            endAdornment: !distinctBCP?.has(
              bcpLayerKey({ brand, category, product: "default" })
            ) && <EndAdornment />,
            children: Array.from(productSet).map((product) => ({
              value: bcpLayerKey({ brand, category, product }),
              label: mapBcpName({ brand, category, product }).product,
              alwaysOpen: true,
              id: stringToNumber(product),
              selectable: distinctBCP?.has(bcpLayerKey({ brand, category, product })),
              endAdornment: !distinctBCP?.has(bcpLayerKey({ brand, category, product })) && (
                <EndAdornment />
              ),
            })),
          })),
        ],
      };

      return brandNode;
    });
  }, [historicalSpendsDTO, distinctBCP, selectedBCP, mapBcpName]);

  const { navigateToScenarioForm } = useScenariosV3Routes();

  // Analytics tracking for this page
  const { trackSpendInputsPageDuration } = useScenarioAnalyticsV3({
    scenario,
  });

  // Track page visit duration for analytics
  usePageVisitTracking(trackSpendInputsPageDuration, {
    trackingInterval: 30_000, // 30 seconds
    minimumDuration: 3_000, // 3 seconds
  });

  // Simple navigation confirmation with react-confirm
  const handleNavigationAttempt = useCallback(
    (nextLocation: Location) => {
      // Use a custom confirm to handle this navigation
      const isNavigationToMainForm = nextLocation.pathname === getScenarioV3FormRoute(uuid);
      if (isNavigationToMainForm) {
        return true;
      }

      const confirmation = confirm("Are you sure you want to leave this page?");
      if (confirmation) {
        mediaConstraintsForm.reset();
        spendInputForm.reset();
        return true;
      }

      return false;
    },
    [mediaConstraintsForm, spendInputForm]
  );

  const handleCancel = async () => {
    const confirmed = await confirmUnsavedChanges({ theme });
    if (confirmed) {
      mediaConstraintsForm.reset();
      spendInputForm.reset();
      navigateToScenarioForm(uuid);
    }
  };

  // Get currency configuration
  const currencySymbol = config.currency.getSymbol();
  const formatCurrency = config.currency.formatCurrency;

  const handleUnitSelect = (option: EnhancedOption | null) => {
    if (option) {
      if (!distinctBCP?.has(option.value)) {
        toast.error(
          "This Business Unit can not be selected as you don't have sufficient spend data. Please select another Business Unit."
        );
        return;
      }

      if (selectedBCP.has(option.value)) {
        toast.info("This Business Unit is already selected.");
        return;
      }

      const bcp = distinctBCP?.get(option.value);
      if (!bcp) {
        console.error("BCP not found", option.value);
        return;
      }
      const { brand, category, product } = bcp;

      const geographyToSet = defaultGeographyFromGeoStatus(geoStatus);

      if (scenario?.scenarioType === ScenarioTypeEnum.Optimizer) {
        const newConstraint: MediaConstraint = {
          brand,
          category,
          product,
          channel: "", // Will be set by user in the table
          maxValue: null!, // Set empty instead of zero so we can see placeholder value
          minValue: null!, // Set empty instead of zero so we can see placeholder value
          geography: geographyToSet,
        };

        handleMediaConstraintsRowChange({
          type: "add",
          value: newConstraint,
        });
      }

      if (scenario?.scenarioType === ScenarioTypeEnum.Budget) {
        const newConstraint: SpendInput = {
          brand,
          category,
          product,
          channel: "", // Will be set by user in the table
          spend: null!, // Set empty instead of zero so we can see placeholder value
          geography: geographyToSet,
        };

        handleSpendInputRowChange({
          type: "add",
          value: newConstraint,
        });
      }
    }
  };

  // Enhanced unit select with dropdown control
  const handleUnitSelectWithDropdown = (option: any) => {
    handleUnitSelect(option);
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  const handleClickAway = () => {
    setIsDropdownOpen(false);
  };

  const handleBack = () => {
    navigateToScenarioForm(uuid);
  };

  const handleSave = async () => {
    await saveForm();
    toast.success("Constraints saved successfully");
    navigateToScenarioForm(uuid);
  };

  const areDatesSelectedOrLoading =
    !!scenarioInformationForm.watch("startDate") && !!scenarioInformationForm.watch("endDate");

  const totalBudget = budgetAllocationForm.watch("totalBudget");

  const isReadyForConstraints = areDatesSelectedOrLoading && !!totalBudget;
  const isMaxTotalExceedingTotalBudget =
    spendInputForm.formState.errors.root?.[ConstraintsMaxTotalExceedsTotalBudgetErrorKey];

  const isMinTotalExceedingTotalBudget =
    mediaConstraintsForm.formState.errors.root?.[ConstraintsMinTotalExceedsTotalBudgetErrorKey];

  const budgetAllocationFormErrors = budgetAllocationForm.formState.errors;

  const totalMinConstraints =
    mediaConstraints?.reduce((acc, input) => acc + input.minValue, 0) ?? 0;

  const totalMaxConstraints =
    mediaConstraints?.reduce((acc, input) => acc + input.maxValue, 0) ?? 0;

  const totalSpend = spendInputs?.reduce((acc, input) => acc + input.spend, 0) ?? 0;

  if (loading) {
    return (
      <Paper>
        <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      {/* Block navigation when there are unsaved changes */}
      <Prompt when={isDirty} message={handleNavigationAttempt} />

      <Box sx={{ position: "relative", flexGrow: 1 }}>
        <Box p={3} pb={20}>
          <Breadcrumbs
            items={[
              {
                label: "All Scenarios",
                to: "/scenarios-v3/all",
              },
              {
                label: scenario?.name ?? "Scenario",
                to: `/scenarios-v3/${uuid}/form`,
              },
              {
                label: "Channel Spend Inputs",
              },
            ]}
          />

          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
            Channel Spend Inputs
          </Typography>
          <Typography variant="body1">
            Set spending rules by funnel stage, business unit, or channel.
          </Typography>

          {!areDatesSelectedOrLoading && (
            <Box mt={2} mb={2}>
              <Typography variant="body2" color="text.secondary">
                Please select start and end dates in the scenario form before adding channel
                constraints.
              </Typography>
            </Box>
          )}

          {/* Show message if total budget is not set */}
          {areDatesSelectedOrLoading && !budgetAllocationForm.watch("totalBudget") && (
            <Box mt={2} mb={2}>
              <Typography variant="body2" color="warning.main">
                Please set a total budget in the scenario form before adding channel constraints.
                Channel constraints cannot exceed the total budget.
              </Typography>
            </Box>
          )}

          <Divider sx={{ marginTop: 2, marginBottom: 3 }} />

          <InfoBanner />

          {scenario?.scenarioType === ScenarioTypeEnum.Optimizer &&
            historicalSpends &&
            !showGeoTable && (
              <OptimizerConstraintsTable
                historicalSpendsDTO={historicalSpendsDTO ?? []}
                form={mediaConstraintsForm}
                disabled={!isReadyForConstraints}
                currencySymbol={currencySymbol}
                historicalSpends={historicalSpends}
                isConstraintsFromApiEnabled={isConstraintsFromApiEnabled}
                formatCurrency={formatCurrency}
              />
            )}

          {scenario?.scenarioType === ScenarioTypeEnum.Optimizer &&
            historicalSpends &&
            showGeoTable && (
              <OptimizerGeographyConstraintsTable
                historicalSpendsDTO={historicalSpendsDTO ?? []}
                disabled={!isReadyForConstraints}
                currencySymbol={currencySymbol}
                isConstraintsFromApiEnabled={isConstraintsFromApiEnabled}
                formatCurrency={formatCurrency}
              />
            )}

          {scenario?.scenarioType !== ScenarioTypeEnum.Optimizer &&
            historicalSpends &&
            !showGeoTable && (
              <ForecastConstraintsTable
                historicalSpendsDTO={historicalSpendsDTO ?? []}
                form={spendInputForm}
                disabled={!isReadyForConstraints}
                currencySymbol={currencySymbol}
                historicalSpends={historicalSpends}
                isConstraintsFromApiEnabled={isConstraintsFromApiEnabled}
                formatCurrency={formatCurrency}
              />
            )}

          {scenario?.scenarioType !== ScenarioTypeEnum.Optimizer &&
            historicalSpends &&
            showGeoTable && (
              <ForecastGeographySpendsTable
                historicalSpendsDTO={historicalSpendsDTO ?? []}
                form={spendInputForm}
                disabled={!isReadyForConstraints}
                currencySymbol={currencySymbol}
                isConstraintsFromApiEnabled={isConstraintsFromApiEnabled}
                formatCurrency={formatCurrency}
              />
            )}

          {/* Business Unit Button */}
          <Box mt={2}>
            <OutlinedButton
              ref={buttonRef}
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setIsDropdownOpen(true)}
              disabled={!isReadyForConstraints}
            >
              Business Unit
            </OutlinedButton>

            {/* Business Unit Dropdown */}
            <Popper
              open={isDropdownOpen}
              anchorEl={buttonRef.current}
              placement="bottom-start"
              style={{ zIndex: 1300, width: 400, maxWidth: "100%" }}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box>
                  {businessUnitHierarchyOptions.length === 0 ? (
                    <Paper elevation={3} sx={{ mt: 1, p: 2 }}>
                      <Typography>No business units available</Typography>
                    </Paper>
                  ) : (
                    <>
                      <HierarchicalAutocomplete
                        options={businessUnitHierarchyOptions}
                        onChange={handleUnitSelectWithDropdown}
                        disableClearable={true}
                        openOnFocus={true}
                        open={true}
                        renderInput={(params) => (
                          <Box ref={params.InputProps.ref}>
                            <input {...params.inputProps} style={{ display: "none" }} />
                          </Box>
                        )}
                      />
                    </>
                  )}
                </Box>
              </ClickAwayListener>
            </Popper>
          </Box>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              maxWidth: `${TOP_NAV_CONTENT_MAX_WIDTH_PX}px`,
              minWidth: `${TOP_NAV_CONTENT_MIN_WIDTH_PX}px`,
              margin: "0 auto",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Stack direction="row" justifyContent="space-between" gap={2} p={2}>
              <Stack direction="row" gap={2}>
                <FormControl fullWidth margin="normal">
                  <Typography fontWeight={600} mb={1} variant="body1">
                    Total Budget
                  </Typography>
                  <Controller
                    name={"totalBudget"}
                    control={budgetAllocationForm.control}
                    render={({ field, fieldState }) => (
                      <>
                        <DollarTextField
                          allowNegative={false}
                          fullWidth={true}
                          error={
                            !!fieldState.error ||
                            !!isMaxTotalExceedingTotalBudget ||
                            !!isMinTotalExceedingTotalBudget
                          }
                          id={"totalBudget"}
                          name={"totalBudget"}
                          size="small"
                          disabled={!isDefined(historicalSpends?.maxTotal)}
                          value={field.value ?? ""}
                          onChange={(value) =>
                            handleTotalBudgetChange("totalBudget", value ?? null)
                          }
                          onBlur={field.onBlur}
                          currencySymbol={currencySymbol}
                        />
                        {isMaxTotalExceedingTotalBudget && (
                          <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                            {isMaxTotalExceedingTotalBudget.message}
                          </BaseTypography>
                        )}

                        {isMinTotalExceedingTotalBudget && (
                          <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                            {isMinTotalExceedingTotalBudget.message}
                          </BaseTypography>
                        )}

                        {budgetAllocationFormErrors.totalBudget?.message && (
                          <BaseTypography variant="body2" color={brandColors.delete} mt={1}>
                            {budgetAllocationFormErrors.totalBudget.message}
                          </BaseTypography>
                        )}
                      </>
                    )}
                  />
                </FormControl>
                {scenario?.scenarioType === ScenarioTypeEnum.Optimizer && (
                  <>
                    <FormControl fullWidth margin="normal">
                      <Typography fontWeight={600} mb={1} variant="body1">
                        Total Min Constraints
                      </Typography>
                      <DollarTextField
                        value={totalMinConstraints}
                        disabled={true}
                        size="small"
                        currencySymbol={currencySymbol}
                        onChange={() => {}}
                      />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                      <Typography fontWeight={600} mb={1} variant="body1">
                        Total Max Constraints
                      </Typography>
                      <DollarTextField
                        value={totalMaxConstraints}
                        disabled={true}
                        size="small"
                        currencySymbol={currencySymbol}
                        onChange={() => {}}
                      />
                    </FormControl>
                  </>
                )}

                {scenario?.scenarioType === ScenarioTypeEnum.Budget && (
                  <>
                    <FormControl fullWidth margin="normal">
                      <Typography fontWeight={600} mb={1} variant="body1">
                        Total Spend
                      </Typography>
                      <DollarTextField
                        value={totalSpend}
                        disabled={true}
                        size="small"
                        currencySymbol={currencySymbol}
                        onChange={() => {}}
                      />
                    </FormControl>
                  </>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" gap={2}>
                {isDirty ? (
                  <OutlinedButton onClick={handleCancel} disabled={!isReadyForConstraints}>
                    Cancel
                  </OutlinedButton>
                ) : (
                  <OutlinedButton onClick={handleBack} disabled={!isReadyForConstraints}>
                    Back
                  </OutlinedButton>
                )}

                <PrimaryButton
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!isReadyForConstraints || isSaving}
                >
                  {isSaving ? "Saving..." : "Save Constraints"}
                </PrimaryButton>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ChannelSpendPage;
